import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * ImageLightbox Component
 *
 * A modal lightbox for viewing full-size images.
 * Features:
 * - Full-screen modal overlay
 * - Click outside or ESC to close
 * - Navigation between multiple images
 * - Zoom controls
 * - Image download option
 * - Loading state
 */
function ImageLightbox({
  images = [],
  initialIndex = 0,
  isOpen = false,
  onClose
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const imageRef = useRef(null);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setIsLoading(true);
      setIsZoomed(false);
    }
  }, [isOpen, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose?.();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, currentIndex, images.length]);

  const goToPrevious = useCallback(() => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      setIsLoading(true);
      setIsZoomed(false);
    }
  }, [images.length]);

  const goToNext = useCallback(() => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      setIsLoading(true);
      setIsZoomed(false);
    }
  }, [images.length]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  const handleDownload = () => {
    const currentImage = images[currentIndex];
    if (!currentImage) return;

    const link = document.createElement('a');
    link.href = currentImage.data;
    link.download = currentImage.name || `image-${currentIndex + 1}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-xs"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
        aria-label="Close lightbox"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Top toolbar */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        {/* Image counter */}
        {images.length > 1 && (
          <span className="px-3 py-1 bg-black/50 rounded-full text-white text-sm">
            {currentIndex + 1} / {images.length}
          </span>
        )}

        {/* Image name */}
        {currentImage.name && (
          <span className="px-3 py-1 bg-black/50 rounded-full text-white text-sm max-w-[200px] truncate">
            {currentImage.name}
          </span>
        )}
      </div>

      {/* Bottom toolbar */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 flex items-center gap-2">
        {/* Zoom button */}
        <button
          onClick={toggleZoom}
          className={`p-2 rounded-full transition-colors ${
            isZoomed
              ? 'bg-white/20 text-white'
              : 'bg-black/50 text-white/70 hover:text-white hover:bg-black/70'
          }`}
          aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
          title={isZoomed ? 'Zoom out' : 'Zoom in'}
        >
          {isZoomed ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          )}
        </button>

        {/* Download button */}
        <button
          onClick={handleDownload}
          className="p-2 bg-black/50 text-white/70 hover:text-white hover:bg-black/70 rounded-full transition-colors"
          aria-label="Download image"
          title="Download"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
      </div>

      {/* Previous button */}
      {images.length > 1 && (
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          aria-label="Previous image"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Next button */}
      {images.length > 1 && (
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          aria-label="Next image"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Image container */}
      <div
        className={`relative max-w-[90vw] max-h-[85vh] ${
          isZoomed ? 'cursor-zoom-out overflow-auto' : 'cursor-zoom-in'
        }`}
        onClick={toggleZoom}
      >
        {/* Loading spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Image */}
        <img
          ref={imageRef}
          src={currentImage.data}
          alt={currentImage.name || `Image ${currentIndex + 1}`}
          onLoad={handleImageLoad}
          className={`
            transition-all duration-200 select-none
            ${isLoading ? 'opacity-0' : 'opacity-100'}
            ${isZoomed ? 'max-w-none max-h-none' : 'max-w-[90vw] max-h-[85vh] object-contain'}
          `}
          draggable={false}
        />
      </div>

      {/* Thumbnail strip for multiple images */}
      {images.length > 1 && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-2 bg-black/50 rounded-lg max-w-[80vw] overflow-x-auto">
          {images.map((img, index) => (
            <button
              key={img.id || index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
                setIsLoading(true);
                setIsZoomed(false);
              }}
              className={`
                w-12 h-12 shrink-0 rounded-md overflow-hidden border-2 transition-all
                ${index === currentIndex
                  ? 'border-white scale-110'
                  : 'border-transparent opacity-60 hover:opacity-100'
                }
              `}
              aria-label={`View image ${index + 1}`}
            >
              <img
                src={img.data}
                alt={img.name || `Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageLightbox;
